<template>
	<Swiper class="slide-list" v-bind="mainSwiperOptions" @swiper="onSwiper">
		<SwiperSlide
			v-for="(item, index) in props.data"
			:key="`${index}`"
			:class="['container_item', 'cursor-pointer', { on: findMatchingIndex(String(category_pk)) === index }]"
			@click="onClickCard(item.hot_ranking_pk, item.hot_ranking_category_pk)"
		>
			<img class="container_item_img" :src="item.hot_ranking_image_path" alt="hot_ranking_image" loading="lazy" />

			<div class="container_item_content">
				<p class="title">{{ item.hot_ranking_name }}</p>
				<p class="sub">{{ item.hot_ranking_description }}</p>
			</div>
		</SwiperSlide>
		<SwiperSlide v-if="props.data.length % 2 !== 0" :class="['container_item_none']"> </SwiperSlide>
		<div class="swiper-pagination"></div>
	</Swiper>
</template>
<script setup lang="ts">
//swiper
// core
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { FreeMode, Navigation, Pagination } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/vue';

import { hotrankingCategorys } from '@/api/hotRanking';

// define
const router = useRouter();
const route = useRoute();
const props = defineProps<{
	data: hotrankingCategorys[];
}>();
const category_pk = computed(() => route.params.pk); //카테고리 pk
const swiperComponent = ref();

// 해당 마스터로 슬라이드 이동
const onSwiper = (swiper: SwiperOptions) => {
	swiperComponent.value = swiper;
};
const onClickCard = (pk: number, categoryPk: number) => {
	router.push({
		params: { pk, categoryPk },
		query: {
			tab1: route.query.tab1,
			category: route.query.category,
		},
	});
};
const findMatchingIndex = (queryPk: string) => {
	return props.data.findIndex((item) => String(item.hot_ranking_pk) === String(queryPk));
};

const mainSwiperOptions: SwiperOptions = {
	modules: [FreeMode, Pagination, Navigation],
	slidesPerView: 2,
	spaceBetween: 10,
	slidesPerGroup: 2,
	freeMode: false,
	observer: true,
	observeParents: true,
	updateOnWindowResize: true,
	pagination: {
		el: '.swiper-pagination',
		type: 'fraction',
		formatFractionCurrent: function (number: number) {
			return ('0' + number).slice(-2);
		},
		formatFractionTotal: function (number: number) {
			return ('0' + number).slice(-2);
		},
		renderFraction: (currentClass: string, totalClass: string) => {
			return (
				'<div class="swiper-pagination_wrapper"><div class="swiper_list"><div class="swiper-button-prev"></div><span class="' +
				currentClass +
				'"></span>' +
				'<span class="swiper-pagination-line">' +
				' | ' +
				'</span>' +
				'<span class="' +
				totalClass +
				'"></span><div class="swiper-button-next"></div></div>'
			);
		},
		clickable: true,
	},
	navigation: {
		nextEl: '.swiper-button-next',
		prevEl: '.swiper-button-prev',
	},
	loop: true,
};

onMounted(() => {
	swiperComponent.value?.slideTo(findMatchingIndex(String(category_pk.value)));
});
</script>

<style lang="scss" scoped>
.slide-list {
	width: 100%;
	/* height: 22rem; */
	display: flex;
	flex-direction: column;
	gap: 1.6rem;
	:deep(.swiper-wrapper) {
		height: fit-content;
		display: grid;
		grid-auto-flow: column;
	}

	.container_item {
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 1.6rem;
		height: 100%;
		// justify-content: center;
		gap: 1rem;
		border: 1px solid var(--color-base-mid);
		border-radius: 8px;
		font-size: var(--font-s-mid);
		font-weight: var(--font-w-semi-bold);
		line-height: normal;
		opacity: 0.6;
		&_img {
			width: 6rem;
			height: 6rem;
			overflow: hidden;
			img {
				width: 100%;
				height: 100%;
				object-fit: contain;
			}
		}
		&_content {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 0.4rem;

			.sub {
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
				color: var(--color-base-light);
				text-align: center;
			}
		}
	}
	.on {
		border: 1px solid var(--color-base-white);
		opacity: 1;
	}
	:deep(.swiper-pagination) {
		width: 100%;
		font-weight: var(--font-w-regular);
		font-size: var(--font-s-small);
		color: #fff;
		position: unset;
		.swiper-pagination_wrapper {
			display: flex;
			justify-content: flex-end;
			max-width: 128rem;
			margin: 0 auto;
			.swiper_list {
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 1rem;
				height: 2.6rem;
				width: 12.2rem;
				border-radius: 10rem;
			}
			.swiper-pagination-line {
				color: var(--color-base-mid);
			}
			.swiper-pagination-current {
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
				line-height: 1.551rem;
			}
			.swiper-pagination-total {
				color: var(--color-base-light);
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
				line-height: 1.551rem;
			}
			.swiper-button-prev {
				position: unset;
				margin-top: 0;
				height: inherit;
				width: 2.2rem;
			}
			.swiper-button-next {
				position: unset;
				margin-top: 0;
				height: inherit;
				width: 2.2rem;
			}
			.swiper-button-prev::after {
				color: var(--color-base-white);
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
				line-height: 1.551rem;
			}
			.swiper-button-next::after {
				color: var(--color-base-white);
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
				line-height: 1.551rem;
			}
		}
	}
}
</style>
